/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

@file:Suppress("DuplicatedCode", "ObjectPropertyName", "Detekt:TooManyFunctions", "ClassName")
@file:KlangScript.Library("sprudel")

package io.peekandpoke.klang.sprudel.lang

import io.peekandpoke.klang.audio_bridge.constants.PHASER_CENTER_HZ
import io.peekandpoke.klang.audio_bridge.constants.PHASER_FLOOR
import io.peekandpoke.klang.audio_bridge.constants.PHASER_RATE_HZ
import io.peekandpoke.klang.audio_bridge.constants.PHASER_SWEEP_HZ
import io.peekandpoke.klang.audio_bridge.constants.PHASER_WET
import io.peekandpoke.klang.script.annotations.KlangScript
import io.peekandpoke.klang.script.ast.CallInfo
import io.peekandpoke.klang.sprudel.SprudelPattern
import io.peekandpoke.klang.sprudel.SprudelVoiceData
import io.peekandpoke.klang.sprudel._applyControlFromParams
import io.peekandpoke.klang.sprudel._liftOrReinterpretNumericalField
import io.peekandpoke.klang.sprudel._mapNumericField
import io.peekandpoke.klang.sprudel.katalystParamsOrNew
import io.peekandpoke.klang.sprudel.lang.SprudelDslArg.Companion.asSprudelDslArgs
import io.peekandpoke.klang.sprudel.putKatalystParam

// -- phaser, the wet slot (the head) --------------------------------------------------------------------------------

// Every phaser slot writes the voice field AND the orbit chain's matching slot (`phaser.wet`, ...),
// which is what makes this door an alias of `katp` on a DECLARED chain (signal-flow plan §7, Katalyst
// step 5a).
//
// A REST calls no setter at all and leaves both sources alone, and no setter here has a CLEAR arm.
// No numeric TAIL setter of a compound BUS door can be handed a null: `_liftNumericField` returns early
// on a control value that is not a number and `_mapNumericField` skips a null mapping (the
// 2026-09-16 rule). The WET is this door's head setter (since step 3d(iii), 2026-09-24), so the
// bare-call reinterpret CAN hand it one, and it returns on the spot (`?: return@voiceSetter`),
// writing nothing, like the heads of `reverb` and `delay`. No setter of any compound bus door
// clears on a null.

/**
 * Fills the phaser stage's companions from `constants/BusEffectDefaults.kt` (`/dsl-design` §4 is the
 * rule; this is only what THIS door does). Called from every one of the five setters, because the
 * phaser has no name knob.
 *
 * Reaching for the phaser still makes no sound on its own, and that is the point of [PHASER_WET]
 * being 0.0: the engine gates the sweep on the depth, so a `phaser(rate = 2)` fills a wet of zero
 * and stays inaudible. The fill is about the companions being right the moment the depth arrives.
 *
 * Fills the voice FIELDS with the same five constants, so the field accessors (`phaser.wet`) read
 * what the slots hold. The fields no longer cross the wire (the per-voice phaser that read them
 * retired in phase 3 step 9, the wire fields with it); the orbit reads the slots.
 *
 * One consequence worth knowing, and it holds for every filled door (`duck` included since step
 * 5b-3, when its accessors moved onto the slots its fill writes): a filled knob becomes
 * READABLE through its field accessor, so `phaser.wet` reads 0.0 after `phaser(rate = 2)` where it
 * used to read nothing. A cross-stage read such as `delay(wet = phaser.wet)` therefore engages the
 * delay line at a zero send rather than leaving it alone. No shipped song reads one stage into
 * another this way.
 */
private fun SprudelVoiceData.fillPhaserDefaults() {
    val slots = katalystParamsOrNew()

    slots.setOrDefault("phaser.rate", value = null, default = PHASER_RATE_HZ)
    slots.setOrDefault("phaser.wet", value = null, default = PHASER_WET)
    slots.setOrDefault("phaser.center", value = null, default = PHASER_CENTER_HZ)
    slots.setOrDefault("phaser.sweep", value = null, default = PHASER_SWEEP_HZ)
    slots.setOrDefault("phaser.floor", value = null, default = PHASER_FLOOR)

    if (phaserRate == null) {
        phaserRate = PHASER_RATE_HZ
    }

    if (phaserDepth == null) {
        phaserDepth = PHASER_WET
    }

    if (phaserCenter == null) {
        phaserCenter = PHASER_CENTER_HZ
    }

    if (phaserSweep == null) {
        phaserSweep = PHASER_SWEEP_HZ
    }

    if (phaserFloor == null) {
        phaserFloor = PHASER_FLOOR
    }
}

private val phaserWetMutation = voiceSetter {
    val wet = it?.asDoubleOrNull() ?: return@voiceSetter

    phaserDepth = wet
    putKatalystParam("phaser.wet", wet)
    fillPhaserDefaults()
}

private fun applyPhaserWet(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.phaserDepth }, update = phaserWetMutation)
    }

    return source._liftOrReinterpretNumericalField(args, phaserWetMutation)
}


/**
 * The orbit phaser: depth, rate, centre, sweep and dry floor.
 *
 * One sweep over the summed orbit bus, the DAW-insert model, so every
 * knob belongs to the orbit's owning voice: a voice that sets phaser knobs without owning its orbit
 * is not phased at all. Route it to its own orbit to give it its own phaser.
 *
 * The dry signal stays untouched by default (`floor` is 1), so `wet` ADDS the swept notch on top
 * rather than crossfading into it. Lower `floor` to turn `wet` back into a crossfade.
 *
 * `wet` comes first, as on every door that has one, and the LFO rate second: `phaser(0.5, 2)` is
 * half depth at 2 Hz.
 *
 * The call sets every slot: the ones you leave out take their shared defaults, rate 0, wet 0,
 * centre 1000 Hz, sweep 1000 Hz and floor 1, unless an earlier call already set them. Wet 0 is the
 * engine's own gate, so reaching for the phaser without a depth is still silent, exactly as before.
 * Slots apply in order, wet first, so a mapper on a later slot sees a default an earlier slot of
 * the same call filled in. A wet MAPPER runs before any other knob can fill the wet, so it maps
 * nothing on a fresh note: `phaser(rate = 2, wet = add(0.3))` is wet 0, the rate's fill. Map the
 * wet in a later call, once it is set: `phaser(rate = 2).phaser(wet = add(0.3))` is wet 0.3.
 *
 * Every slot is independent and patternable; an omitted slot keeps its value, a named slot takes
 * a mapper (`phaser(wet = mul(2))`), and the numeric slots read back as `phaser.wet`, `phaser.rate`, `phaser.center`, `phaser.sweep`, `phaser.floor`.
 * With no argument at all, the pattern's own values are reinterpreted as `wet`; a value that is not
 * a number writes nothing.
 *
 * ```KlangScript(Playable)
 * note("c3 e3").s("saw").phaser(0.5, 0.5)                                 // depth and rate
 * ```
 *
 * ```KlangScript(Playable)
 * note("c3 e3").s("saw").phaser(0.5, 0.5).phaser(rate = mul("1 4"))        // the second note swirls faster
 * ```
 *
 * ```KlangScript(Playable)
 * note("c3 e3").s("saw").phaser("0.2 0.8", 0.5).delay(wet = phaser.wet, time = 0.25)   // as much delay as phaser
 * ```
 *
 * @param wet Depth, 0 to 1. Engages above 0.01.
 * @param rate LFO rate in Hz.
 * @param center Centre frequency of the sweep, Hz.
 * @param sweep Sweep range around the centre, Hz.
 * @param floor Minimum dry share kept in the mix, 0 to 1.
 * @tool SprudelPhaserEditor
 * @tool rate: SprudelPhaserEditor, SprudelPhaserSequenceEditor
 *
 * @scope orbit
 * @category effects
 * @tags phaser, wet, rate, center, sweep, floor
 */
@KlangScript.Function
fun SprudelPattern.phaser(
    wet: PatternLike? = null,
    rate: PatternLike? = null,
    center: PatternLike? = null,
    sweep: PatternLike? = null,
    floor: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern {
    // A call without a wet must not touch wet: reinterpret runs only on a fully bare call.
    var p = if (wet != null || !(rate != null || center != null || sweep != null || floor != null)) {
        applyPhaserWet(this, listOfNotNull(wet).asSprudelDslArgs(callInfo))
    } else {
        this
    }
    if (rate != null) p = applyPhaserRate(p, listOf<Any?>(rate).asSprudelDslArgs(callInfo?.forParam(1)))
    if (center != null) p = applyPhaserCenter(p, listOf<Any?>(center).asSprudelDslArgs(callInfo?.forParam(2)))
    if (sweep != null) p = applyPhaserSweep(p, listOf<Any?>(sweep).asSprudelDslArgs(callInfo?.forParam(3)))
    if (floor != null) p = applyPhaserFloor(p, listOf<Any?>(floor).asSprudelDslArgs(callInfo?.forParam(4)))
    return p
}

/** Parses this string as a pattern, then applies [phaser]. */
@KlangScript.Function
fun String.phaser(
    wet: PatternLike? = null,
    rate: PatternLike? = null,
    center: PatternLike? = null,
    sweep: PatternLike? = null,
    floor: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).phaser(wet, rate, center, sweep, floor, callInfo)

/** Chains a [phaser] step onto this [PatternMapperFn]. */
@KlangScript.Function
fun PatternMapperFn.phaser(
    wet: PatternLike? = null,
    rate: PatternLike? = null,
    center: PatternLike? = null,
    sweep: PatternLike? = null,
    floor: PatternLike? = null,
    callInfo: CallInfo? = null
): PatternMapperFn =
    this.chain { p -> p.phaser(wet, rate, center, sweep, floor, callInfo) }

/**
 * The `phaser` object: `phaser(...)` sets the slots, and each numeric slot reads back as a child,
 * `phaser.rate`, `phaser.wet`, `phaser.center`, `phaser.sweep`, `phaser.floor`.
 *
 * @scope orbit
 * @category effects
 * @tags phaser, accessor
 */
@KlangScript.Library("sprudel")
@KlangScript.Object("phaser")
object phaser {

    /** The rate slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val rate: FieldAccessor = FieldAccessor { it.phaserRate }

    /** The wet slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val wet: FieldAccessor = FieldAccessor { it.phaserDepth }

    /** The center slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val center: FieldAccessor = FieldAccessor { it.phaserCenter }

    /** The sweep slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val sweep: FieldAccessor = FieldAccessor { it.phaserSweep }

    /** The floor slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val floor: FieldAccessor = FieldAccessor { it.phaserFloor }

    /**
     * The setter, see [SprudelPattern.phaser].
     *
     * @param wet Depth, 0 to 1. Engages above 0.01.
     * @param rate LFO rate in Hz.
     * @param center Centre frequency of the sweep, Hz.
     * @param sweep Sweep range around the centre, Hz.
     * @param floor Minimum dry share kept in the mix, 0 to 1.
     */
    @KlangScript.Invoke
    operator fun invoke(
        wet: PatternLike? = null,
        rate: PatternLike? = null,
        center: PatternLike? = null,
        sweep: PatternLike? = null,
        floor: PatternLike? = null,
        callInfo: CallInfo? = null
    ): PatternMapperFn =
        { p -> p.phaser(wet, rate, center, sweep, floor, callInfo) }
}

// -- phaser.rate -----------------------------------------------------------------------------------------------------

private val phaserRateMutation = voiceSetter {
    phaserRate = it?.asDoubleOrNull()
    putKatalystParam("phaser.rate", phaserRate)
    fillPhaserDefaults()
}

private fun applyPhaserRate(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.phaserRate }, update = phaserRateMutation)
    }

    return source._liftOrReinterpretNumericalField(args, phaserRateMutation)
}

// -- phaser.floor ----------------------------------------------------------------------------------------------------

private val phaserFloorMutation = voiceSetter {
    phaserFloor = it?.asDoubleOrNull()
    putKatalystParam("phaser.floor", phaserFloor)
    fillPhaserDefaults()
}

private fun applyPhaserFloor(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.phaserFloor }, update = phaserFloorMutation)
    }

    return source._liftOrReinterpretNumericalField(args, phaserFloorMutation)
}

// -- phaser.center ---------------------------------------------------------------------------------------------------

private val phaserCenterMutation = voiceSetter {
    phaserCenter = it?.asDoubleOrNull()
    putKatalystParam("phaser.center", phaserCenter)
    fillPhaserDefaults()
}

private fun applyPhaserCenter(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.phaserCenter }, update = phaserCenterMutation)
    }

    return source._liftOrReinterpretNumericalField(args, phaserCenterMutation)
}

// -- phaser.sweep ----------------------------------------------------------------------------------------------------

private val phaserSweepMutation = voiceSetter {
    phaserSweep = it?.asDoubleOrNull()
    putKatalystParam("phaser.sweep", phaserSweep)
    fillPhaserDefaults()
}

private fun applyPhaserSweep(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.phaserSweep }, update = phaserSweepMutation)
    }

    return source._liftOrReinterpretNumericalField(args, phaserSweepMutation)
}

// -- tremolo.rate ----------------------------------------------------------------------------------------------------

private val tremoloRateMutation = voiceSetter { tremoloRate = it?.asDoubleOrNull() }

private fun applyTremoloRate(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.tremoloRate }, update = tremoloRateMutation)
    }

    return source._liftOrReinterpretNumericalField(args, tremoloRateMutation)
}


/**
 * The tremolo: depth, rate and waveform.
 *
 * Unlike the phaser above it, the tremolo runs per voice: every note gets
 * its own LFO, so two notes on one orbit can wobble at different rates. The LFO is the oscillator
 * of that shape; the square, sawtooth and ramp get a 16 ms soft edge, so they pulse without clicking.
 *
 * Every slot is independent and patternable; an omitted slot keeps its value, a named slot takes
 * a mapper (`tremolo(rate = mul(2))`), and the numeric slots read back as `tremolo.depth` and `tremolo.rate`.
 * With no argument at all, the pattern's own values are reinterpreted as `depth`.
 *
 * ```KlangScript(Playable)
 * note("c3").s("saw").tremolo(0.6, 4)                                     // depth and rate
 * ```
 *
 * ```KlangScript(Playable)
 * note("c3").s("saw").tremolo(0.6, 4).tremolo(rate = mul("<1 2>"))         // twice as fast every other bar
 * ```
 *
 * ```KlangScript(Playable)
 * note("c3 e3").s("saw").tremolo("0.3 0.9", 4).gain(tremolo.depth)         // deeper, louder
 * ```
 *
 * @param depth Depth, 0 to 1. The stage is built only above 0.
 * @param rate LFO rate in Hz. [beatRate] follows the tempo.
 * @param shape LFO waveform: `sine`, `triangle`, `square`, `sawtooth`, `ramp`.
 * @tool SprudelTremoloEditor
 * @tool depth: SprudelTremoloEditor, SprudelTremoloSequenceEditor
 * @tool shape: SprudelWaveformEditor, SprudelWaveformSequenceEditor
 *
 * @scope voice
 * @category effects
 * @tags tremolo, depth, rate, shape
 */
@KlangScript.Function
fun SprudelPattern.tremolo(
    depth: PatternLike? = null,
    rate: PatternLike? = null,
    shape: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern {
    // A tail-only call must not touch depth: reinterpret runs only on a fully bare call.
    var p = if (depth != null || !(rate != null || shape != null)) {
        applyTremoloDepth(this, listOfNotNull(depth).asSprudelDslArgs(callInfo))
    } else {
        this
    }
    if (rate != null) p = applyTremoloRate(p, listOf<Any?>(rate).asSprudelDslArgs(callInfo?.forParam(1)))
    if (shape != null) p = applyTremoloShape(p, listOf<Any?>(shape).asSprudelDslArgs(callInfo?.forParam(2)))
    return p
}

/** Parses this string as a pattern, then applies [tremolo]. */
@KlangScript.Function
fun String.tremolo(
    depth: PatternLike? = null,
    rate: PatternLike? = null,
    shape: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).tremolo(depth, rate, shape, callInfo)

/** Chains a [tremolo] step onto this [PatternMapperFn]. */
@KlangScript.Function
fun PatternMapperFn.tremolo(
    depth: PatternLike? = null,
    rate: PatternLike? = null,
    shape: PatternLike? = null,
    callInfo: CallInfo? = null
): PatternMapperFn =
    this.chain { p -> p.tremolo(depth, rate, shape, callInfo) }

/**
 * The `tremolo` object: `tremolo(...)` sets the slots, and each numeric slot reads back as a child,
 * `tremolo.depth`, `tremolo.rate`.
 *
 * @scope voice
 * @category effects
 * @tags tremolo, accessor
 */
@KlangScript.Library("sprudel")
@KlangScript.Object("tremolo")
object tremolo {

    /** The depth slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val depth: FieldAccessor = FieldAccessor { it.tremoloDepth }

    /** The rate slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val rate: FieldAccessor = FieldAccessor { it.tremoloRate }

    /**
     * The setter, see [SprudelPattern.tremolo].
     *
     * @param depth Depth, 0 to 1. The stage is built only above 0.
     * @param rate LFO rate in Hz. [beatRate] follows the tempo.
     * @param shape LFO waveform: `sine`, `triangle`, `square`, `sawtooth`, `ramp`.
     */
    @KlangScript.Invoke
    operator fun invoke(
        depth: PatternLike? = null,
        rate: PatternLike? = null,
        shape: PatternLike? = null,
        callInfo: CallInfo? = null
    ): PatternMapperFn =
        { p -> p.tremolo(depth, rate, shape, callInfo) }
}

// -- tremolo.depth ---------------------------------------------------------------------------------------------------

private val tremoloDepthMutation = voiceSetter { tremoloDepth = it?.asDoubleOrNull() ?: tremoloDepth }

private fun applyTremoloDepth(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.tremoloDepth }, update = tremoloDepthMutation)
    }

    return source._liftOrReinterpretNumericalField(args, tremoloDepthMutation)
}

// -- tremolo, the shape slot -----------------------------------------------------------------------------------------

private val tremoloShapeMutation = voiceSetter { shape -> tremoloShape = shape?.toString()?.lowercase() }

private fun applyTremoloShape(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    return source._applyControlFromParams(args, tremoloShapeMutation) { src, ctrl ->
        src.tremoloShape = ctrl.tremoloShape
        src
    }
}
